import { QrCodeIcon } from "@phosphor-icons/react/dist/csr/QrCode";
import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { CardBand } from "./card-band";
import InfoPair from "./info-pair";
import { NameAndDate, NameAndDateSkeleton } from "./name-and-date";
import type { ReferralContentProps } from "./referralContent";

interface NewReferralCardContentProps extends ReferralContentProps {
  onAccept: () => void;
  onLost?: () => void;
  onMoveToPaid?: () => void;
  onDelete?: () => void;
}

export const NewReferralCardSkeleton = () => (
  <Stack align="start" gap={4} className="p-4">
    <NameAndDateSkeleton showDate />
    <Stack align="start" gap={2}>
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-4 w-48" />
    </Stack>
    <Skeleton className="h-7 w-full rounded-lg" />
  </Stack>
);

export const NewReferralCardContent = ({
  referral,
  onAccept,
  onLost,
  onMoveToPaid,
  onDelete,
}: NewReferralCardContentProps) => {
  const { businessId } = useBusiness();
  const { data: business } = useGetBusiness(businessId);

  const attributedUserName =
    referral.advocate.signup_source?.display_identification;
  return (
    <Stack className="w-full" align="start" gap={0}>
      {attributedUserName && (
        <Tooltip>
          <TooltipTrigger render={<CardBand />}>
            <QrCodeIcon aria-hidden="true" />
            <Text variant="caption">{attributedUserName}</Text>
          </TooltipTrigger>
          <TooltipContent>
            {business?.name} team member that signed up the advocate that sent
            this referral
          </TooltipContent>
        </Tooltip>
      )}
      <Stack align="start" gap={4} className="w-full p-4">
        <NameAndDate
          referral={referral}
          onLost={onLost}
          onMoveToPaid={onMoveToPaid}
          onDelete={onDelete}
        />
        <Stack align="start" gap={2}>
          <InfoPair label="Services" info={referral.service_needed} />
          <InfoPair
            label="Referred by"
            info={getCustomerNameOrPhone(referral.advocate)}
          />
        </Stack>
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onAccept();
          }}
          className="w-full"
        >
          Accept
        </Button>
      </Stack>
    </Stack>
  );
};
